import { generalErrorText } from "./contants";
import { type AlertTypes } from "./types";

export enum FrontendErrorCodesGeneral {
  UndefinedError = "general/undefined-error",
  NotFound = "general/not-found",
}

/** Offline downloads, SQLite, and secure playback (mobile app). */
export enum FrontendErrorCodesOffline {
  /** Cover image could not be cached during offline download (audio is rolled back). */
  OfflineDownloadCoverImageFailed = "offline/download-cover-image-failed",
  /** App cache directory unavailable (e.g. `cacheDirectory` missing). */
  OfflineDownloadCacheUnavailable = "offline/download-cache-unavailable",
  /** Audio file download failed after retries (network or storage). */
  OfflineDownloadAudioFailed = "offline/download-audio-failed",
  /** SQLite offline DB used before `initializeDownloadDatabase()` (programmer / lifecycle error). */
  OfflineDatabaseNotInitialized = "offline/database-not-initialized",
  /** No completed download row for offline playback. */
  OfflinePlaybackDownloadNotFound = "offline/playback-download-not-found",
  /** AES key missing from secure storage (re-download required). */
  OfflinePlaybackDecryptionKeyMissing = "offline/playback-decryption-key-missing",
  /** SQLite row present but encrypted audio file missing on disk (re-download required). */
  OfflinePlaybackEncryptedFileMissing = "offline/playback-encrypted-file-missing",
}

/** Firestore client: documents, queries, pagination, watched-file logs. */
export enum FrontendErrorCodesFirestore {
  /** Pagination invariant broken (e.g. missing `startAfter` or empty cursor after non-empty page). */
  PaginationInvalid = "general/firestore-pagination-invalid",
  UndefinedDocument = "firebase/undefined-document",
  WatchedFilesLogWrongAssignment = "firebase/watched-files-log-wrong-assignment",
  IntroCourseNotFound = "firebase/intro-course-not-found",
  DocumentNotFound = "firebase/document-not-found",
}

/** Content reference & text-search document shapes (`MediaType` filters). */
export enum FrontendErrorCodesContentSearch {
  InvalidContentTypeForSearch = "general/invalid-content-type-for-search",
}

export enum FrontendErrorCodesImagePicker {
  ImagePickerCanceled = "general/image-picker-canceled",
  ImagePickerNoAsset = "general/image-picker-no-asset",
  ImageBlobFetchFailed = "general/image-blob-fetch-failed",
}

export enum FrontendErrorCodesFavorites {
  FavoriteIdRequired = "general/favorite-id-required",
}

export enum FrontendErrorCodesRecommendedMedia {
  RecommendedMediaUnsupportedType = "general/recommended-media-unsupported-type",
  RecommendedMediaAllFetchesFailed = "general/recommended-media-all-fetches-failed",
}

export enum FrontendErrorCodesPolling {
  PollingError = "general/polling-error",
  PollingTimeout = "general/polling-timeout",
}

/** Cloud Functions / callable HTTPS (not Firestore document reads). */
export enum FrontendErrorCodesFirebase {
  OnboardingCallFailed = "firebase/onboarding-call-failed",
  FunctionCallFailed = "firebase/function-call-failed",
  /** Callable `resource-exhausted` (e.g. offline download throttle); display text comes from the error when present. */
  CallableResourceExhausted = "firebase/callable-resource-exhausted",
}

export enum FrontendErrorCodesFirebaseAuth {
  WrongPassword = "auth/wrong-password",
  InvalidCredential = "auth/invalid-credential",
  UserNotFound = "auth/user-not-found",
  EmailAlreadyInUse = "auth/email-already-in-use",
  EmailRequired = "firebase/email-required",
  PasswordRequired = "firebase/password-required",
  ResetPasswordFailed = "auth/reset-password-failed",
  UpdateEmailFailed = "auth/update-email-failed",
  UpdateDisplayNameFailed = "auth/update-display-name-failed",
  DeleteUserFailed = "auth/delete-user-failed",
  TooManyRequests = "auth/too-many-requests",
  UnknownError = "auth/unknown-error",
  CancelledPopupRequest = "auth/cancelled-popup-request",
  UserMismatch = "auth/user-mismatch",
  NoCurrentUser = "auth/no-current-user",
}

export enum FrontendErrorCodesGoogleSignIn {
  NoIdToken = "auth/google-no-id-token",
  GoogleSignInCancelled = "auth/google-sign-in-cancelled",
  PlayServicesNotAvailable = "auth/google-play-services-not-available",
}

export enum FrontendErrorCodesAppleSignIn {
  AppleSignInCancelled = "ERR_REQUEST_CANCELED",
}

export enum FrontendErrorCodesRevenueCat {
  ProCheckOnServerFailed = "revenuecat/pro-check-on-server-failed",
  PackageNotFound = "revenuecat/package-not-found",
  PurchaseCanceled = "revenuecat/purchase-canceled",
  PurchaseFailed = "revenuecat/purchase-failed",
  RestoreFailed = "revenuecat/restore-failed",
  SubscriptionFoundOnStoreAccount = "revenuecat/subscription-found-on-store-account",
  AlreadyHasActiveSubscription = "revenuecat/already-has-active-subscription",
}

/** CMS (FireCMS) content admin validation. */
export enum FrontendErrorCodesCms {
  LessonDurationExceedsCap = "cms/lesson-duration-exceeds-cap",
  LessonDurationInvalid = "cms/lesson-duration-invalid",
  AudioProcessingFailed = "cms/audio-processing-failed",
}

/** Marketing-site example player (Storyblok). */
export enum FrontendErrorCodesWeb {
  /** Both an audio course id and a daily meditation id are set on one block. */
  ExamplePlayerAmbiguousSource = "web/example-player-ambiguous-source",
}

export type FrontendErrorCodes =
  | FrontendErrorCodesGeneral
  | FrontendErrorCodesOffline
  | FrontendErrorCodesFirestore
  | FrontendErrorCodesContentSearch
  | FrontendErrorCodesImagePicker
  | FrontendErrorCodesFavorites
  | FrontendErrorCodesRecommendedMedia
  | FrontendErrorCodesPolling
  | FrontendErrorCodesFirebase
  | FrontendErrorCodesFirebaseAuth
  | FrontendErrorCodesRevenueCat
  | FrontendErrorCodesGoogleSignIn
  | FrontendErrorCodesAppleSignIn
  | FrontendErrorCodesCms
  | FrontendErrorCodesWeb;

const allErrorEnums = [
  FrontendErrorCodesGeneral,
  FrontendErrorCodesOffline,
  FrontendErrorCodesFirestore,
  FrontendErrorCodesContentSearch,
  FrontendErrorCodesImagePicker,
  FrontendErrorCodesFavorites,
  FrontendErrorCodesRecommendedMedia,
  FrontendErrorCodesPolling,
  FrontendErrorCodesFirebase,
  FrontendErrorCodesFirebaseAuth,
  FrontendErrorCodesGoogleSignIn,
  FrontendErrorCodesAppleSignIn,
  FrontendErrorCodesRevenueCat,
  FrontendErrorCodesCms,
  FrontendErrorCodesWeb,
];

type FrontendErrorCodeMessages = Record<FrontendErrorCodes, { message: string; severity: AlertTypes }>;

// Define the messages object with type checking
export const frontendErrorMessages: FrontendErrorCodeMessages = {
  // General errors
  [FrontendErrorCodesGeneral.UndefinedError]: {
    message: generalErrorText,
    severity: "danger",
  },
  [FrontendErrorCodesGeneral.NotFound]: {
    message: "Der Inhalt konnte nicht gefunden werden.",
    severity: "danger",
  },

  [FrontendErrorCodesOffline.OfflineDownloadCoverImageFailed]: {
    message: "Das Titelbild konnte nicht gespeichert werden. Bitte versuche den Download erneut.",
    severity: "warning",
  },
  [FrontendErrorCodesOffline.OfflineDownloadCacheUnavailable]: {
    message: "Zwischenspeicher nicht verfügbar. Bitte versuche den Download erneut.",
    severity: "warning",
  },
  [FrontendErrorCodesOffline.OfflineDownloadAudioFailed]: {
    message: "Die Audiodatei konnte nicht heruntergeladen werden. Bitte versuche es erneut.",
    severity: "warning",
  },
  [FrontendErrorCodesOffline.OfflineDatabaseNotInitialized]: {
    message: "Die Offline-Datenbank ist nicht bereit. Bitte starte die App neu.",
    severity: "danger",
  },
  [FrontendErrorCodesOffline.OfflinePlaybackDownloadNotFound]: {
    message: "Download nicht gefunden.",
    severity: "warning",
  },
  [FrontendErrorCodesOffline.OfflinePlaybackDecryptionKeyMissing]: {
    message: "Schlüssel nicht gefunden. Bitte erneut herunterladen.",
    severity: "warning",
  },
  [FrontendErrorCodesOffline.OfflinePlaybackEncryptedFileMissing]: {
    message: "Die Offline-Datei fehlt auf dem Gerät. Bitte lade die Übung erneut herunter, um sie offline zu hören.",
    severity: "warning",
  },
  [FrontendErrorCodesFirestore.PaginationInvalid]: {
    message: "Die Inhaltsliste konnte nicht geladen werden. Bitte versuche es erneut.",
    severity: "warning",
  },
  [FrontendErrorCodesFirestore.UndefinedDocument]: {
    message: "Der Inhalt konnte nicht geladen werden.",
    severity: "warning",
  },
  [FrontendErrorCodesFirestore.WatchedFilesLogWrongAssignment]: {
    message: generalErrorText,
    severity: "danger",
  },
  [FrontendErrorCodesFirestore.IntroCourseNotFound]: {
    message: "Der Einführungskurs konnte nicht gefunden werden.",
    severity: "warning",
  },
  [FrontendErrorCodesFirestore.DocumentNotFound]: {
    message: "Der Inhalt konnte nicht gefunden werden.",
    severity: "warning",
  },
  [FrontendErrorCodesContentSearch.InvalidContentTypeForSearch]: {
    message: "Ungültiger Inhaltstyp.",
    severity: "danger",
  },
  [FrontendErrorCodesImagePicker.ImagePickerCanceled]: {
    message: "Auswahl abgebrochen.",
    severity: "warning",
  },
  [FrontendErrorCodesImagePicker.ImagePickerNoAsset]: {
    message: "Kein Bild ausgewählt.",
    severity: "warning",
  },
  [FrontendErrorCodesImagePicker.ImageBlobFetchFailed]: {
    message: "Bild konnte nicht gelesen werden.",
    severity: "warning",
  },
  [FrontendErrorCodesFavorites.FavoriteIdRequired]: {
    message: "Favorit konnte nicht entfernt werden.",
    severity: "warning",
  },
  [FrontendErrorCodesRecommendedMedia.RecommendedMediaUnsupportedType]: {
    message: "Nicht unterstützter Medientyp.",
    severity: "danger",
  },
  [FrontendErrorCodesRecommendedMedia.RecommendedMediaAllFetchesFailed]: {
    message: "Empfehlungen konnten nicht geladen werden.",
    severity: "warning",
  },
  [FrontendErrorCodesPolling.PollingError]: {
    message: generalErrorText,
    severity: "danger",
  },
  [FrontendErrorCodesPolling.PollingTimeout]: {
    message: "Zeitüberschreitung. Bitte versuche es erneut.",
    severity: "warning",
  },

  // Firebase (callable / cloud) errors
  [FrontendErrorCodesFirebase.OnboardingCallFailed]: {
    message: "Die Registrierung ist fehlgeschlagen.",
    severity: "danger",
  },
  [FrontendErrorCodesFirebase.FunctionCallFailed]: {
    message: "Es ist leider etwas schief gegangen.",
    severity: "danger",
  },
  [FrontendErrorCodesFirebase.CallableResourceExhausted]: {
    message: "Download-Limit erreicht. Du darfst täglich 10 Dateien herunterladen.",
    severity: "warning",
  },

  // Firebase Auth errors
  [FrontendErrorCodesFirebaseAuth.WrongPassword]: {
    message: "Das Passwort scheint nicht zu stimmen. Passwort vergessen?",
    severity: "warning",
  },
  [FrontendErrorCodesFirebaseAuth.InvalidCredential]: {
    message: "Das Passwort scheint nicht zu stimmen. Passwort vergessen?",
    severity: "warning",
  },
  [FrontendErrorCodesFirebaseAuth.UserNotFound]: {
    message: "Für diese E-Mail-Adresse existiert leider kein Konto.",
    severity: "warning",
  },
  [FrontendErrorCodesFirebaseAuth.EmailAlreadyInUse]: {
    message: "Diese E-Mail-Adresse wird bereits verwendet",
    severity: "warning",
  },
  [FrontendErrorCodesFirebaseAuth.EmailRequired]: {
    message: "Bitte gib eine E-Mail-Adresse ein.",
    severity: "warning",
  },
  [FrontendErrorCodesFirebaseAuth.PasswordRequired]: {
    message: "Bitte gib ein Passwort ein.",
    severity: "warning",
  },
  [FrontendErrorCodesFirebaseAuth.ResetPasswordFailed]: {
    message: "Das Zurücksetzen des Passworts ist fehlgeschlagen.",
    severity: "danger",
  },
  [FrontendErrorCodesFirebaseAuth.UpdateEmailFailed]: {
    message: "Das Aktualisieren der E-Mail-Adresse ist fehlgeschlagen.",
    severity: "danger",
  },
  [FrontendErrorCodesFirebaseAuth.UpdateDisplayNameFailed]: {
    message: "Das Aktualisieren des Anzeigenamens ist fehlgeschlagen.",
    severity: "danger",
  },
  [FrontendErrorCodesFirebaseAuth.CancelledPopupRequest]: {
    message: "Die Anmeldung wurde abgebrochen.",
    severity: "warning",
  },

  [FrontendErrorCodesFirebaseAuth.DeleteUserFailed]: {
    message: "Das Löschen des Benutzers ist fehlgeschlagen.",
    severity: "danger",
  },
  [FrontendErrorCodesFirebaseAuth.TooManyRequests]: {
    message: "Zu viele Anfragen. Bitte versuche es später erneut oder kontaktiere uns.",
    severity: "danger",
  },
  [FrontendErrorCodesFirebaseAuth.UnknownError]: {
    message: "Ein unbekannter Fehler ist aufgetreten.",
    severity: "danger",
  },
  [FrontendErrorCodesFirebaseAuth.UserMismatch]: {
    message: "Die E-Mail-Adresse oder das Passwort sind nicht korrekt.",
    severity: "danger",
  },
  [FrontendErrorCodesFirebaseAuth.NoCurrentUser]: {
    message: "Du bist nicht angemeldet. Bitte melde dich erneut an.",
    severity: "warning",
  },

  // Google Sign-In errors
  [FrontendErrorCodesGoogleSignIn.GoogleSignInCancelled]: {
    message: "Die Anmeldung mit Google wurde abgebrochen",
    severity: "warning",
  },
  [FrontendErrorCodesGoogleSignIn.PlayServicesNotAvailable]: {
    message: "Google Play Service nicht verfügbar oder veraltet.",
    severity: "danger",
  },
  [FrontendErrorCodesGoogleSignIn.NoIdToken]: {
    message: "Es wurde kein idToken von Google Sign-In zurückgegeben",
    severity: "danger",
  },

  // Apple Sign-In errors
  [FrontendErrorCodesAppleSignIn.AppleSignInCancelled]: {
    message: "Die Anmeldung mit Apple wurde abgebrochen",
    severity: "warning",
  },

  // RevenueCat errors
  [FrontendErrorCodesRevenueCat.PackageNotFound]: {
    message: "Das gewählte Paket konnte nicht gefunden werden.",
    severity: "danger",
  },
  [FrontendErrorCodesRevenueCat.PurchaseCanceled]: {
    message: "Du hast den Kauf abgebrochen.",
    severity: "warning",
  },
  [FrontendErrorCodesRevenueCat.PurchaseFailed]: {
    message: "Der Kauf ist fehlgeschlagen. Bitte versuche es später erneut oder kontaktiere uns.",
    severity: "danger",
  },
  [FrontendErrorCodesRevenueCat.ProCheckOnServerFailed]: {
    message: "Dein Kauf war erfolgreich, aber wir konnten deinen Account nicht aktualisieren. Bitte kontaktiere uns.",
    severity: "danger",
  },
  [FrontendErrorCodesRevenueCat.RestoreFailed]: {
    message: "Etwas ist schief gelaufen. Bitte versuche es später erneut oder kontaktiere uns.",
    severity: "danger",
  },
  [FrontendErrorCodesRevenueCat.SubscriptionFoundOnStoreAccount]: {
    message:
      'Du hast bereits ein Abo auf deinem Store-Account, welches nicht mit dem eingeloggten Account übereinstimmt. Bestätige die Übertragung auf diesen Account indem du auf "Kauf transferieren" klickst.',
    severity: "warning",
  },
  [FrontendErrorCodesRevenueCat.AlreadyHasActiveSubscription]: {
    message: "Du hast bereits ein Pro-Abo. Ein zweites Abo ist nicht nötig.",
    severity: "warning",
  },

  [FrontendErrorCodesCms.LessonDurationExceedsCap]: {
    message: "Audio ist zu lang. Bitte kürzen und erneut hochladen.",
    severity: "warning",
  },
  [FrontendErrorCodesCms.LessonDurationInvalid]: {
    message: "Die Audiodauer konnte nicht ermittelt werden. Bitte Datei prüfen und erneut versuchen.",
    severity: "warning",
  },
  [FrontendErrorCodesCms.AudioProcessingFailed]: {
    message: "Audio konnte nicht verarbeitet werden. Bitte Datei prüfen und erneut versuchen.",
    severity: "danger",
  },

  [FrontendErrorCodesWeb.ExamplePlayerAmbiguousSource]: {
    message: "Bitte nur einen Audiokurs oder eine Tagesmeditation auswählen.",
    severity: "warning",
  },
};

export class FrontendError extends Error {
  code: FrontendErrorCodes;
  severity: AlertTypes;

  constructor(message: string, code: FrontendErrorCodes, severity: AlertTypes = "danger") {
    super(message);
    this.name = "FrontendError";
    this.code = code;
    this.severity = severity;

    Object.setPrototypeOf(this, FrontendError.prototype);
  }
}
export const getFrontendErrorMessageFromCode = (errorCodeString: FrontendErrorCodes | string): string => {
  if (Object.hasOwn(frontendErrorMessages, errorCodeString)) {
    return frontendErrorMessages[errorCodeString as FrontendErrorCodes].message;
  }
  return generalErrorText;
};

export const getFrontendErrorCodeFromErrorObject = (error: FrontendErrorWithCode): FrontendErrorCodes => {
  const errorCode = error.code;

  for (const errorEnum of allErrorEnums) {
    if (Object.values(errorEnum).includes(errorCode as FrontendErrorCodes)) {
      return errorCode as FrontendErrorCodes;
    }
  }
  // If no match is found, return a default error code
  return FrontendErrorCodesGeneral.UndefinedError;
};

export const getFrontendErrorSeverityFromCode = (errorCode: FrontendErrorCodes | string): AlertTypes => {
  if (Object.hasOwn(frontendErrorMessages, errorCode)) {
    return frontendErrorMessages[errorCode as FrontendErrorCodes].severity;
  }
  return "danger";
};
interface FrontendErrorWithCode {
  message: string;
  code: string;
}

export const isFrontendErrorWithCode = (error: unknown): error is FrontendErrorWithCode => {
  return typeof error === "object" && error !== null && "code" in error;
};
